import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import {FormControl, FormGroup, Validators} from "@angular/forms";
import {PatientAccountService} from "../../../services/patient.account.service";
import {takeUntilDestroy} from "take-until-destroy";
import {
  ContactType,
  IOneTimeCodeDetails,
  IOneTimeCodeGenerationResponse,
  IOneTimeCodeValidationRequest
} from "../common/common.types";
import {LogManager} from "../../../../common/log/log.manager";
import {LogCat} from "../../../../common/log/log.categories";
import {Observable, ReplaySubject} from "rxjs";
import {tap} from "rxjs/operators";

@Component({
  selector: 'app-one-time-code',
  templateUrl: './one.time.code.component.html',
  styleUrls: ['./one.time.code.component.scss']
})
export class OneTimeCodeComponent implements OnInit, OnDestroy {
  @ViewChild("OneTimeCode", {static: true}) oneTimeCodeEl: ElementRef;
  @Output() back = new EventEmitter();
  @Output() next = new EventEmitter();
  @Output() error = new EventEmitter();

  public formControls: FormGroup;
  public patient: any;
  public patientContactInfo: string;
  public patientContactType: string;
  public newPatient: boolean;
  public isCodeFilled = '';

  public notificationBoxText1 = '';
  public notificationBoxText2 = '';
  public notificationBoxIconType = '';
  public resendDisabled = false;

  constructor(private patientAccountSvc: PatientAccountService,
              private changeDetector: ChangeDetectorRef) { }

  ngOnInit() {
    this.formControls = new FormGroup({
      oneTimeCode: new FormControl('', {validators: [Validators.required]})
    });
  }

  ngOnDestroy() {
  }

  public generateOneTimeCode(patient: any, newPatient: boolean): Observable<IOneTimeCodeGenerationResponse> {
    this.patient = patient;
    this.newPatient = newPatient;
    this.formControls.reset();
    this.isCodeFilled = '';
    this.resetNotificationBoxText();
    if (!this.resendDisabled) {
      return this.patientAccountSvc.generateOneTimeCode(patient)
        .pipe(takeUntilDestroy(this), tap((res: IOneTimeCodeGenerationResponse) => {
          if (res.success) {
            this.setContactInfo(res.contactInfo, res.contactType);
            this.disableResendForPeriod(10);
          }
        }));
    } else {
        let subj = new ReplaySubject<any>();
        subj.next({success: true});
        return subj;
    }
  }

  public resendOneTimeCode() {
    if (!this.resendDisabled) {
      this.disableResendForPeriod(10);
      this.patientAccountSvc.generateOneTimeCode(this.patient)
        .pipe(takeUntilDestroy(this)).subscribe((res: IOneTimeCodeGenerationResponse) => {
        this.oneTimeCodeEl.nativeElement.value = '';
        this.notificationBoxText1 = 'NewOneTimeCodeWasSent';
        this.notificationBoxText2 = '';
        this.notificationBoxIconType = 'pp-icon-info';
        if (res.success) {
          this.setContactInfo(res.contactInfo, res.contactType);
        }
        this.changeDetector.markForCheck();
      });
    }
  }

  public continue() {
    if (this.isValid()) {
      const oneTimeCodeValidationRequest: IOneTimeCodeValidationRequest = {
        clinicalRequest: {model:{patient:this.patient}},
        oneTimeCode: this.formControls.controls['oneTimeCode'].value,
      };
      this.patientAccountSvc.validateOneTimeCode(oneTimeCodeValidationRequest)
        .pipe(takeUntilDestroy(this)).subscribe( res => {
          if (!res.valid) {
            this.setError('OneTimeCodeIsInValid');
            this.oneTimeCodeEl.nativeElement.value = '';
            this.changeDetector.markForCheck();
          } else {
            const oneTimeCodeData: IOneTimeCodeDetails = {
              clinicalRequest: {model:{patient:this.patient}},
              oneTimeCode: oneTimeCodeValidationRequest.oneTimeCode,
            };
            this.next.emit(oneTimeCodeData);
          }
        },
        err => {
          LogManager.error(LogCat.Login.CreatePatientAccount, err);
          this.error.emit(this.patient);
        });
    }
  }

  public goback() {
    this.back.emit(this.patient);
  }

  private disableResendForPeriod(seconds: number) {
    this.resendDisabled = true;
    this.changeDetector.markForCheck();
    setTimeout(()=> {
      this.resendDisabled = false;
      this.changeDetector.markForCheck();
    }, seconds * 1000);
  }

  private setContactInfo(contactInfo: string, contactType: ContactType) {
    if (contactType === ContactType.Email) {
      this.patientContactType = 'Email';
      this.patientContactInfo = contactInfo;
    } else {
      this.patientContactType = 'MobilePhone';
      this.patientContactInfo = contactInfo;
    }
  }

  private isValid(): boolean {
    this.isCodeFilled = 'valid';
    if (this.formControls.controls['oneTimeCode'].invalid) {
      this.isCodeFilled = 'invalid';
      return false;
    }
    return true;
  }

  private setError(error: string) {
    this.notificationBoxText1 = error;
    this.notificationBoxText2 = 'NewOneTimeCodeWasSent';
    this.notificationBoxIconType = 'pp-icon-error';
  }

  private resetNotificationBoxText() {
    this.notificationBoxText1 = '';
    this.notificationBoxText2 = '';
  }
}
